import { Icon } from '@iconify/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColumn } from 'src/components/GridWrapper';
import useWindowSize from 'src/hooks/useWindowSize';
import AttributesCell from 'src/sections/catalog_products/table_cell/AttributesCell';
import AttributesFilter from 'src/sections/catalog_products/table_cell/AttributesFilter';
import BrandNameCell from 'src/sections/catalog_products/table_cell/BrandNameCell';
import CasePackCell from 'src/sections/catalog_products/table_cell/CasePackCell';
import DescriptionCell from 'src/sections/catalog_products/table_cell/DescriptionCell';
import ItemNumCell from 'src/sections/catalog_products/table_cell/ItemNumCell';
import LastPurchaseDateCell from 'src/sections/catalog_products/table_cell/LastPurchaseDateCell';
import MfgNumberCell from 'src/sections/catalog_products/table_cell/MfgNumberCell';
import PriceCell from 'src/sections/catalog_products/table_cell/PriceCell';
import QtyCell from 'src/sections/catalog_products/table_cell/QtyCell';
import SupplierCell from 'src/sections/catalog_products/table_cell/SupplierCell';
import SwapItem from 'src/sections/catalog_products/table_cell/SwapItem';
import UomCell from 'src/sections/catalog_products/table_cell/UomCell';
import WeightCell from 'src/sections/catalog_products/table_cell/WeightCell';
import { ViewCatalogProductsContext } from 'src/sections/catalog_products/ViewCatalogProductsContext';
import { useContextSelector } from 'use-context-selector';

const useProductColumns = (maxNumberOfAttributes: number) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const selectedCatalog = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.selectedCatalog
  );
  const isOfflineSupplier = useMemo(() => selectedCatalog?.catalogType === 3, [selectedCatalog]);
  const isExternalSupplier = useMemo(() => selectedCatalog?.catalogType === 2, [selectedCatalog]);
  const isSupplierUser = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.isSupplierUser
  );

  // Calculate responsive widths based on screen size using percentage-based approach
  // Base width ensures columns don't get too small on smaller screens
  const gridWidth = useMemo(() => Math.max(width, 1200), [width]);

  // Helper function to calculate width based on percentage
  const setPercentage = useMemo(
    () => (percentage: number) => Math.round((gridWidth / 100) * percentage),
    [gridWidth]
  );

  const columns: GridColumn<any>[] = useMemo(
    () => [
      ...((!isExternalSupplier && !isSupplierUser
        ? [
            {
              field: 'selected',
              title: '',
              filter: 'boolean',
              menu: false,
              sortable: false,
              width: setPercentage(4),
            },
          ]
        : []) as GridColumn<any>[]),
      ...((!isSupplierUser
        ? [
            {
              field: 'isNoItem',
              title: '',
              menu: false,
              sortable: false,
              width: 40,
              component: SwapItem,
            },
          ]
        : []) as GridColumn<any>[]),
      {
        field: 'qty',
        title: t('Qty'),
        sortable: false,
        filterable: false,
        menu: false,
        width: setPercentage(7),
        show: true,
        component: QtyCell,
      },
      {
        field: 'attributes',
        title: t('Attributes'),
        filter: 'text',
        sortable: false,
        menu: true,
        width: Math.max(maxNumberOfAttributes * 32, setPercentage(9)),
        show: true,
        component: AttributesCell,
        columnMenu: AttributesFilter,
      },
      {
        field: 'itemNum',
        title: t('Item #'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(8),
        show: true,
        component: ItemNumCell,
      },
      {
        field: 'brandName',
        title: t('Brand Name'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(9),
        show: true,
        component: BrandNameCell,
      },
      {
        field: 'description',
        title: t('Description'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(19),
        show: true,
        component: DescriptionCell,
      },
      ...((isOfflineSupplier
        ? [
            {
              field: 'companyName',
              title: t('Supplier'),
              filter: 'text',
              sortable: true,
              menu: true,
              width: setPercentage(8),
              show: true,
              component: SupplierCell,
            },
          ]
        : []) as GridColumn<any>[]),
      {
        field: 'uom',
        title: t('UOM'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(10),
        show: true,
        component: UomCell,
      },
      {
        field: 'casePack',
        title: t('Case Pack'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(8),
        show: true,
        component: CasePackCell,
      },
      {
        field: 'manufacturerNumber',
        title: t('Mfg #'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(8),
        show: true,
        component: MfgNumberCell,
      },
      {
        field: 'price',
        title: t('Price'),
        filter: 'numeric',
        format: '{0:c2}',
        filterFormat: 'c',
        dataType: 'dollar',
        sortable: true,
        menu: true,
        width: setPercentage(8),
        show: true,
        component: PriceCell,
      },
      {
        field: 'weight',
        title: t('Wt.'),
        filter: 'text',
        dataType: 'string',
        sortable: true,
        menu: true,
        width: setPercentage(10),
        show: true,
        component: WeightCell,
      },
      {
        field: 'lastPurchaseDate',
        headerClassName: 'cursor-pointer',
        headerCell: () => (
          <>
            <span
              className="k-column-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={t('Favorite Last Purchased')}
            >
              {t('Favorite Last Purchased')}
            </span>
            <Icon
              icon="mdi:information-outline"
              className="cursor-pointer"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={t('favoriteLastPurchasedTooltip')}
            />
          </>
        ),
        filter: 'date',
        sortable: true,
        menu: true,
        width: setPercentage(12),
        show: true,
        component: LastPurchaseDateCell,
      },
    ],
    [isExternalSupplier, isOfflineSupplier, isSupplierUser, maxNumberOfAttributes, setPercentage, t]
  );

  return columns;
};

export default useProductColumns;
